import { useState, useMemo, useEffect } from "react";
import React from "react";
import axios from "axios";
import { API_CONSTANT_ONBOARDING, DEV_BASE } from "utils";
import { getOnboardingHeaders } from "pages/NewOnboarding/shared/api";
import { showGlobalModal } from "components/GlobalModal";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import * as XLSX from "xlsx";
import FormWrapper from "components/FormWrapper";
import ResetAndSubmit from "components/ResetAndSubmit";
import ColumnMapper from "./components/ColumnMapper";
import DataReview from "./components/DataReview";
import { startIdentifyTour, startHeaderCheckTour, startMappingTour, startReviewTour } from "./tours";
import "driver.js/dist/driver.css";
//import BulkOnboardingResultModal from "./components/BulkOnboardingResultModal";
import { motion, AnimatePresence } from "framer-motion";
import {
  SvgAsset1,
  SvgAsset2,
  SvgAsset3,
  SvgAsset4,
  SvgAsset5,
  SvgAsset6,
  SvgAsset7,
  SvgAsset8,
  SvgAsset9,
  SvgAsset10,
  SvgAsset11,
  SvgAsset12,
  SvgAsset13,
  SvgAsset14,
  SvgAsset15,
  SvgAsset16,
  SvgAsset17,
  SvgAsset18,
  SvgAsset19,
  SvgAsset20,
  SvgAsset21,
  SvgAsset22,
  SvgAsset23,
  SvgAsset24,
} from "../../shared/generated-icons/phase2__components__steps__DataSetup__index.icons";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Save,
  Table,
  Wand2,
  FileUp, IdCard,
  X,
  ChevronDown,
  FileText,
  Check,
  ArrowRight,
  Database,
  Download,
  Sparkles,
  CircleCheck,
  Play,
  Send,
  ArrowLeft,
  GraduationCap,
  Users,
  Contact,
  Wallet,
  SquareUser,
  IndianRupee,
} from "lucide-react";
type WizardStage =
  | "UPLOAD"
  | "AUTO_REVIEW"
  | "IDENTIFY"
  | "HEADER_CHECK"
  | "MAPPING"
  | "REVIEW"
  | "DONE";
type SheetType =
  | "STUDENT"
  | "PARENT"
  | "STUDENT_PARENT"
  | "STAFF"
  | "FEE_ARREARS"
  | "IGNORE"
  | null;

interface SheetInfo {
  name: string;
  data: any[];
  type: SheetType;
  mapping?: { [key: string]: string };
}

const aadharValidation = Yup.string()
  .transform((value, originalValue) =>
    originalValue ? String(originalValue).trim() : "",
  )
  .test(
    "is-valid-aadhar",
    "Invalid Aadhar: Must be 12 digits and cannot start with 0 or 1",
    (val) => {
      if (!val) return true;
      return /^[2-9]\d{11}$/.test(val);
    },
  )
  .nullable();

const stringOrNumber = Yup.mixed()
  .transform((value, originalValue) =>
    originalValue ? String(originalValue).trim() : "",
  )
  .nullable();

const parentSchema = Yup.object({
  FIRST_NAME: Yup.string().nullable(),
  FATHER_NAME: Yup.string().nullable(),
  MOBILE_NUMBER: stringOrNumber,
  PHONE: stringOrNumber,
  S_NO: Yup.mixed().nullable(),
  MIDDLE_NAME: Yup.string().nullable(),
  LAST_NAME: Yup.string().nullable(),
  GENDER: Yup.string().nullable(),
  LANGUAGE: Yup.string().nullable(),
  PROFESSION: Yup.string().nullable(),
  COUNTRY_CODE: Yup.string().nullable(),
  EMAIL_ID: Yup.string().email("Invalid email format").nullable(),
  AADHAR_NUMBER: aadharValidation,
  BIRTH_DATE: Yup.string().nullable(),
  RELATION: Yup.string().nullable(),
});

const studentSchema = Yup.object({
  student: Yup.object({
    FIRST_NAME: Yup.string().required("First Name is required"),
    LAST_NAME: Yup.string().nullable(),
    EMAIL_ID: Yup.string().email("Invalid email format").nullable(),
    MOBILE_NUMBER: stringOrNumber,
    GRADE: Yup.string().nullable(),
    ADMISSION_NUMBER: stringOrNumber,
    S_NO: Yup.mixed().nullable(),
    ROLL_NUMBER: stringOrNumber,
    MIDDLE_NAME: Yup.string().nullable(),
    GENDER: Yup.string().nullable(),
    CATEGORY: Yup.string().nullable(),
    SUB_CATEGORY: Yup.string().nullable(),
    BLOOD_GROUP: Yup.string().nullable(),
    AADHAR_NUMBER: aadharValidation,
    LANGUAGE: Yup.string().nullable(),
    BIRTH_DATE: Yup.string().nullable(),
    ADMISSION_DATE: Yup.string().nullable(),
    COUNTRY_CODE: Yup.string().nullable(),
    ADDRESS: Yup.string().nullable(),
    CITY: Yup.string().nullable(),
    PINCODE: stringOrNumber,
    CLASS: Yup.string().nullable(),
    FEE_PAYMENT_DURATION_NAME: Yup.string().nullable(),
    SECONDARY_LANGUAGE: Yup.string().nullable(),
    MOTHER_TONGUE: Yup.string().nullable(),
    IDENTIFICATION_MARKS: Yup.string().nullable(),
    RELATION: Yup.string().nullable(),
    PREVIOUS_INSTITUTE_NAME: Yup.string().nullable(),
    PREVIOUS_INSTITUTE_ADDRESS: Yup.string().nullable(),
    PREVIOUS_YEAR_OF_PASSING: stringOrNumber,
    SCHOLARSHIP_ID: Yup.string().nullable(),
    PHYSICAL_DISABILITY: Yup.string().nullable(),
    WHATSAPP_NUMBER: stringOrNumber,
  }),
  parents: Yup.array().of(parentSchema).nullable(),
});

const staffSchema = Yup.object({
  FIRST_NAME: Yup.string().required("First Name is required"),
  GENDER: Yup.string()
    .uppercase()
    .oneOf(
      ["MALE", "FEMALE", "OTHER"],
      "Gender must be MALE, FEMALE, or OTHER",
    ),
  MOBILE_NUMBER: stringOrNumber,
  COUNTRY_CODE: Yup.string().nullable(),
  EMAIL_ID: Yup.string().email("Invalid email format").nullable(),
  BIRTH_DATE: Yup.string().nullable(),
  JOINING_DATE: Yup.string().nullable(),
  ADDRESS: Yup.string().nullable(),
  CITY: Yup.string().nullable(),
  PINCODE: stringOrNumber,
  QUALIFICATION: Yup.string().nullable(),
  LANGUAGE: Yup.string().nullable(),
  MIDDLE_NAME: Yup.string().nullable(),
  LAST_NAME: Yup.string().nullable(),
  AADHAR_NUMBER: stringOrNumber,
  STAFF_CODE: stringOrNumber,
  TEACHING_SINCE: Yup.number().nullable(),
  ROLES: Yup.array().of(Yup.string()).nullable(),
});

const feeArrearsSchema = Yup.object({
  ADMISSION_NUMBER: Yup.string().nullable(),
  MOBILE_NUMBER: stringOrNumber,
  AADHAR_NUMBER: stringOrNumber,
  AMOUNT: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" ? undefined : Number(originalValue),
    )
    .typeError("Amount must be a number"),
});

const unifiedColumns = [
  { id: "student_S_NO", label: "S.No (Student)" },
  { id: "student_ROLL_NUMBER", label: "Roll Number", required: true },
  { id: "student_FIRST_NAME", label: "First Name", required: true },
  { id: "student_MIDDLE_NAME", label: "Middle Name" },
  { id: "student_LAST_NAME", label: "Last Name" },
  { id: "student_GENDER", label: "Gender", required: true },
  { id: "student_CATEGORY", label: "Category" },
  { id: "student_SUB_CATEGORY", label: "Sub-Category" },
  { id: "student_BLOOD_GROUP", label: "Blood Group" },
  { id: "student_AADHAR_NUMBER", label: "Aadhar Number" },
  { id: "student_LANGUAGE", label: "Language", required: true },
  { id: "student_BIRTH_DATE", label: "Birth Date" },
  { id: "student_ADMISSION_NUMBER", label: "Admission Number", required: true },
  { id: "student_ADMISSION_DATE", label: "Admission Date" },
  { id: "student_MOBILE_NUMBER", label: "Mobile Number", required: true },
  { id: "student_COUNTRY_CODE", label: "Country Code", required: true },
  { id: "student_EMAIL_ID", label: "Email ID" },
  { id: "student_ADDRESS", label: "Address", required: true },
  { id: "student_CITY", label: "City", required: true },
  { id: "student_PINCODE", label: "Pincode" },
  { id: "parent_FATHER_AADHAR", label: "Father Aadhar" },
  { id: "parent_MOTHER_AADHAR", label: "Mother Aadhar" },
  { id: "parent_FATHER_NAME", label: "Father Name" },
  { id: "parent_MOTHER_NAME", label: "Mother Name" },
  { id: "parent_FATHER_MOBILE", label: "Father Mobile" },
  { id: "parent_MOTHER_MOBILE", label: "Mother Mobile" },
  { id: "parent_FATHER_EMAIL", label: "Father Email" },
  { id: "parent_MOTHER_EMAIL", label: "Mother Email" },
  { id: "parent_FATHER_PROFESSION", label: "Father Profession" },
  { id: "parent_MOTHER_PROFESSION", label: "Mother Profession" },
  { id: "parent_FATHER_DOB", label: "Father DOB" },
  { id: "parent_MOTHER_DOB", label: "Mother DOB" },
  { id: "parent_PARENT_LANGUAGE", label: "Parent Language" },
  { id: "parent_GUARDIAN_AADHAR", label: "Guardian Aadhar" },
  { id: "student_CLASS", label: "Class", required: true },
  {
    id: "student_FEE_PAYMENT_DURATION_NAME",
    label: "Fee Payment Duration",
    required: true,
  },
  { id: "student_SECONDARY_LANGUAGE", label: "Secondary Language" },
  { id: "student_MOTHER_TONGUE", label: "Mother Tongue" },
  { id: "student_IDENTIFICATION_MARKS", label: "Identification Marks" },
  { id: "student_RELIGION", label: "Religion" },
  { id: "student_PREVIOUS_INSTITUTE_NAME", label: "Previous Institute Name" },
  {
    id: "student_PREVIOUS_INSTITUTE_ADDRESS",
    label: "Previous Institute Address",
  },
  { id: "student_PREVIOUS_YEAR_OF_PASSING", label: "Previous Year of Passing" },
  { id: "student_SCHOLARSHIP_ID", label: "Scholarship ID" },
  { id: "student_PHYSICAL_DISABILITY", label: "Physical Disability" },
  { id: "student_WHATSAPP_NUMBER", label: "WhatsApp Number" },

  { id: "parent_S_NO", label: "S.No (Parent)" },
  { id: "parent_FIRST_NAME", label: "Parent First Name" },
  { id: "parent_MIDDLE_NAME", label: "Parent Middle Name" },
  { id: "parent_LAST_NAME", label: "Parent Last Name" },
  { id: "parent_GENDER", label: "Parent Gender" },
  { id: "parent_LANGUAGE", label: "Parent Language" },
  { id: "parent_PROFESSION", label: "Profession" },
  { id: "parent_MOBILE_NUMBER", label: "Parent Mobile Number" },
  { id: "parent_COUNTRY_CODE", label: "Parent Country Code" },
  { id: "parent_EMAIL_ID", label: "Parent Email ID" },
  { id: "parent_AADHAR_NUMBER", label: "Parent Aadhar Number" },
  { id: "parent_BIRTH_DATE", label: "Parent Birth Date" },
  { id: "parent_RELATION", label: "Relation" },

  { id: "staff_FIRST_NAME", label: "Staff First Name", required: true },
  { id: "staff_GENDER", label: "Staff Gender", required: true },
  { id: "staff_MOBILE_NUMBER", label: "Staff Mobile Number" },
  { id: "staff_COUNTRY_CODE", label: "Staff Country Code" },
  { id: "staff_EMAIL_ID", label: "Staff Email ID" },
  { id: "staff_BIRTH_DATE", label: "Staff Birth Date" },
  { id: "staff_JOINING_DATE", label: "Staff Joining Date" },
  { id: "staff_ADDRESS", label: "Staff Address" },
  { id: "staff_CITY", label: "Staff City" },
  { id: "staff_PINCODE", label: "Staff Pincode" },
  { id: "staff_QUALIFICATION", label: "Staff Qualification" },
  { id: "staff_LANGUAGE", label: "Staff Language" },
  { id: "staff_MIDDLE_NAME", label: "Staff Middle Name" },
  { id: "staff_LAST_NAME", label: "Staff Last Name" },
  { id: "staff_AADHAR_NUMBER", label: "Staff Aadhar Number" },
  { id: "staff_STAFF_CODE", label: "Staff Code" },
  { id: "staff_TEACHING_SINCE", label: "Teaching Since (Year/Number)" },
  { id: "staff_ROLES", label: "Staff Roles (comma separated)" },

  { id: "arrears_ADMISSION_NUMBER", label: "Admission Number" },
  { id: "arrears_MOBILE_NUMBER", label: "Mobile Number" },
  { id: "arrears_AADHAR_NUMBER", label: "Aadhar Number" },
  { id: "arrears_AMOUNT", label: "Amount", required: true },
];

const AIManagmentHub = ({
  handleNavigate,
  resetFormData,
  disableForm,
}: {
  handleNavigate: any;
  resetFormData?: any;
  disableForm?: boolean;
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [stage, setStage] = useState<WizardStage>(() =>
    sessionStorage.getItem("phase2DataSetupCompleted") === "true"
      ? "DONE"
      : "UPLOAD",
  );
  const [sheets, setSheets] = useState<SheetInfo[]>([]);
  const [processingQueue, setProcessingQueue] = useState<SheetInfo[]>([]);
  const [currentSheetIndex, setCurrentSheetIndex] = useState(0);
  const [finalPayload, setFinalPayload] = useState<{
    STUDENTS: any[];
    PARENTS: any[];
    STAFF: any[];
    FEE_ARREARS: any[];
  }>({ STUDENTS: [], PARENTS: [], STAFF: [], FEE_ARREARS: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [mappedData, setMappedData] = useState<any[]>([]);
  const [savedMappings, setSavedMappings] = useState<
    Partial<Record<SheetType, { mapping: any; fixedValues: any }>>
  >({});
  const [autoProcessedData, setAutoProcessedData] = useState<any>(null);
  const [isAutoProcessing, setIsAutoProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [jobProgress, setJobProgress] = useState<{
    message: string;
    percent: number;
    stage?: string;
    sheets?: Record<string, { status: 'done' | 'processing' | 'pending' | 'skipped' | 'ready'; index?: number; records?: number }>;
    current_entity?: string;
    entities?: Record<string, { status: string; total: number; success?: number; failed?: number }>;
  }>({ message: "Queued...", percent: 0 });
  const [bulkOnboardProgress, setBulkOnboardProgress] = useState<{
    message: string;
    percent: number;
    current_entity?: string;
    entities?: {
      students?: { status: string; total: number; success?: number; failed?: number };
      parents?: { status: string; total: number; success?: number; failed?: number };
      staff?: { status: string; total: number; success?: number; failed?: number };
      arrears?: { status: string; total: number; success?: number; failed?: number };
    };
  }>({ message: "Queued...", percent: 0 });


  const [bulkSubmissionMode, setBulkSubmissionMode] = useState<"auto" | "manual">(
    "auto",
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalData, setResultModalData] = useState<any>(null);
  const [isRetryUpload, setIsRetryUpload] = useState(false);
  const [activeTab, setActiveTab] = useState("students");
  const [sheetValidData, setSheetValidData] = useState<Record<number, any[]>>(

    {},
  );


  useEffect(() => {
    sessionStorage.setItem("aiHubCurrentStage", stage);
    window.dispatchEvent(
      new CustomEvent("formUpdated", { detail: { aiHubStage: stage } }),
    );
  }, [stage]);

  useEffect(() => {
    const handleTourRequest = () => {
      switch (stage) {
        case "IDENTIFY": startIdentifyTour(); break;
        case "HEADER_CHECK": startHeaderCheckTour(); break;
        case "MAPPING": startMappingTour(); break;
        case "REVIEW": startReviewTour(); break;
      }
    };
    window.addEventListener("start-onboarding-tour", handleTourRequest);
    return () => window.removeEventListener("start-onboarding-tour", handleTourRequest);
  }, [stage]);

  const handleDownload = () => {
    // Make sure we actually have the data from the API response
    if (!resultModalData || !resultModalData.data) {
      console.warn("No result data available to download.");
      return;
    }

    const responseData = resultModalData.data;

    // Method 1: Download via direct URL if provided
    if (responseData.excel_file_url) {
      window.open(responseData.excel_file_url, "_blank");
      return;
    }

    // Method 2: Download via Base64 string if provided
    if (responseData.master_file_base64) {
      const linkSource = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${responseData.master_file_base64}`;
      const downloadLink = document.createElement("a");
      downloadLink.href = linkSource;
      downloadLink.download = responseData.filename || "failed_records.xlsx";
      document.body.appendChild(downloadLink); // Append to body for cross-browser compatibility
      downloadLink.click();
      document.body.removeChild(downloadLink); // Clean up
    }
  };

  const handleDownloadSaved = () => {
    if (!resultModalData?.data) return;

    const { students, parents, staff, fee_arrears } = resultModalData.data;

    // Check if there's actually any data to download
    if (
      !students?.length &&
      !parents?.length &&
      !staff?.length &&
      !fee_arrears?.length
    ) {
      showGlobalModal("No saved records to download.", "info");
      return;
    }

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Add sheets for whichever arrays have data
    if (students?.length) {
      const ws = XLSX.utils.json_to_sheet(students);
      XLSX.utils.book_append_sheet(wb, ws, "Students");
    }
    if (parents?.length) {
      const ws = XLSX.utils.json_to_sheet(parents);
      XLSX.utils.book_append_sheet(wb, ws, "Parents");
    }
    if (staff?.length) {
      const ws = XLSX.utils.json_to_sheet(staff);
      XLSX.utils.book_append_sheet(wb, ws, "Staff");
    }
    if (fee_arrears?.length) {
      const ws = XLSX.utils.json_to_sheet(fee_arrears);
      XLSX.utils.book_append_sheet(wb, ws, "Fee Arrears");
    }

    // Trigger the download
    XLSX.writeFile(wb, "Successfully_Saved_Records.xlsx");
  };
  const transformSheetData = (
    headers: any[],
    rawData: any[],
    type: SheetType,
    mapping: any,
    fixedValues: any,
  ) => {
    const normalizeHeader = (h: any) =>
      String(h ?? "")
        .replace(/\u00A0/g, " ")
        .trim()
        .toLowerCase();

    const headerIndexByNormalized = new Map<string, number>();
    headers.forEach((h, idx) => {
      const norm = normalizeHeader(h);
      if (norm && !headerIndexByNormalized.has(norm)) {
        headerIndexByNormalized.set(norm, idx);
      }
    });

    return rawData.map((row: any[]) => {
      const obj: any = { student: {}, parents: [] };
      const parentDetails: any = {};

      Object.entries(mapping).forEach(
        ([systemId, csvHeader]: [string, any]) => {
          if (!csvHeader) return;
          const headerIdx =
            typeof csvHeader === "string"
              ? (headerIndexByNormalized.get(normalizeHeader(csvHeader)) ??
                headers.indexOf(csvHeader))
              : headers.indexOf(csvHeader);
          if (headerIdx !== -1) {
            const value = row[headerIdx];
            if (value !== undefined && value !== null && value !== "") {
              if (type === "PARENT") {
                if (!systemId.startsWith("parent_")) return;
              } else if (type === "STUDENT") {
                if (
                  systemId.startsWith("parent_") ||
                  systemId.startsWith("staff_") ||
                  systemId.startsWith("arrears_")
                )
                  return;
              } else if (type === "STAFF") {
                if (!systemId.startsWith("staff_")) return;
              } else if (type === "FEE_ARREARS") {
                if (!systemId.startsWith("arrears_")) return;
              } else if (type === "STUDENT_PARENT") {
                if (
                  systemId.startsWith("staff_") ||
                  systemId.startsWith("arrears_")
                )
                  return;
              }

              if (systemId.startsWith("student_")) {
                const field = systemId.replace("student_", "");
                obj.student[field] = value;
              } else if (systemId.startsWith("parent_")) {
                const field = systemId.replace("parent_", "");
                parentDetails[field] = value;
              } else if (systemId.startsWith("staff_")) {
                const field = systemId.replace("staff_", "");
                if (field === "ROLES" && typeof value === "string") {
                  obj[field] = value.split(/[, ]+/).filter(Boolean);
                } else {
                  obj[field] = value;
                }
              } else if (systemId.startsWith("arrears_")) {
                const field = systemId.replace("arrears_", "");
                if (field === "AMOUNT") {
                  obj[field] = parseFloat(value);
                } else {
                  obj[field] = value;
                }
              }
            }
          }
        },
      );

      console.log("Fixed Values:", fixedValues);
      Object.entries(fixedValues).forEach(
        ([systemId, value]: [string, any]) => {
          if (value !== undefined && value !== null && value !== "") {
            if (systemId.startsWith("student_")) {
              const field = systemId.replace("student_", "");
              obj.student[field] = value;
              console.log(`Applied fixed value for ${field}:`, value);
            } else if (systemId.startsWith("parent_")) {
              const field = systemId.replace("parent_", "");
              parentDetails[field] = value;
            } else if (systemId.startsWith("staff_")) {
              const field = systemId.replace("staff_", "");
              if (field === "ROLES" && typeof value === "string") {
                obj[field] = value.split(/[, ]+/).filter(Boolean);
              } else {
                obj[field] = value;
              }
            } else if (systemId.startsWith("arrears_")) {
              const field = systemId.replace("arrears_", "");
              if (field === "AMOUNT") {
                obj[field] = parseFloat(value);
              } else {
                obj[field] = value;
              }
            }
          }
        },
      );

      if (!obj.student.COUNTRY_CODE) {
        obj.student.COUNTRY_CODE = "+91";
      }

      if (parentDetails.FATHER_NAME || parentDetails.FATHER_MOBILE) {
        const father: any = {
          FIRST_NAME: parentDetails.FATHER_NAME,
          MOBILE_NUMBER: parentDetails.FATHER_MOBILE,
          EMAIL_ID: parentDetails.FATHER_EMAIL,
          PROFESSION: parentDetails.FATHER_PROFESSION,
          AADHAR_NUMBER: parentDetails.FATHER_AADHAR,
          BIRTH_DATE: parentDetails.FATHER_DOB,
          LANGUAGE: parentDetails.PARENT_LANGUAGE || obj.student.LANGUAGE,
          COUNTRY_CODE:
            parentDetails.COUNTRY_CODE || obj.student.COUNTRY_CODE || "+91",
          RELATION: "Father",
          GENDER: "Male",
        };

        if (father.FIRST_NAME || father.MOBILE_NUMBER) {
          obj.parents.push(father);
        }
      }

      if (parentDetails.MOTHER_NAME || parentDetails.MOTHER_MOBILE) {
        const mother: any = {
          FIRST_NAME: parentDetails.MOTHER_NAME,
          MOBILE_NUMBER: parentDetails.MOTHER_MOBILE,
          EMAIL_ID: parentDetails.MOTHER_EMAIL,
          PROFESSION: parentDetails.MOTHER_PROFESSION,
          AADHAR_NUMBER: parentDetails.MOTHER_AADHAR,
          BIRTH_DATE: parentDetails.MOTHER_DOB,
          LANGUAGE: parentDetails.PARENT_LANGUAGE || obj.student.LANGUAGE,
          COUNTRY_CODE:
            parentDetails.COUNTRY_CODE || obj.student.COUNTRY_CODE || "+91",
          RELATION: "Mother",
          GENDER: "Female",
        };
        if (mother.FIRST_NAME || mother.MOBILE_NUMBER) {
          obj.parents.push(mother);
        }
      }

      if (type === "STAFF") {
        if (!obj.COUNTRY_CODE) {
          obj.COUNTRY_CODE = "+91";
        }
        const { student, parents, ...staffData } = obj;
        return staffData;
      } else if (type === "FEE_ARREARS") {
        const { student, parents, ...arrearsData } = obj;
        return arrearsData;
      }

      if (type === "PARENT") {
        return obj.parents;
      }

      return obj;
    });
  };

  const parseFileForManualMode = (file: File) => {
    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, {
        type: "binary",
        cellDates: true,
        dateNF: "yyyy-mm-dd",
      });
      const sheetList: SheetInfo[] = wb.SheetNames.map((name) => ({
        name,
        data: XLSX.utils.sheet_to_json(wb.Sheets[name], {
          header: 1,
          raw: false,
          dateNF: "yyyy-mm-dd",
        }),
        type: null,
      }));
      setSheets(sheetList);
      setStage("IDENTIFY");
      setIsLoading(false);
    };
    reader.readAsBinaryString(file);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFile(file);
    setIsLoading(true);
    setIsAutoProcessing(true);
    setJobProgress({ message: "Uploading file...", percent: 0 });
    const isRetryJob = { current: false };

    let sessionData: any = {};
    try {
      sessionData = JSON.parse(sessionStorage.getItem("_DATA_") || "{}");
    } catch (e) {
      console.error("Error parsing _DATA_ from session", e);
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Step 1: Enqueue the job
      const enqueueResponse = await axios.post(
        `${API_CONSTANT_ONBOARDING}/data/process-excel`,
        formData,
        {
          params: {
            branch_id: sessionData.BRANCH_ID,
            institute_id: sessionData.INSTITUTE_ID,
            database_name: sessionData.DATABASE_NAME,
          },
          headers: {
            ...getOnboardingHeaders(sessionData),
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { job_id } = enqueueResponse.data.data;
      if (!job_id) throw new Error("No job_id returned from server");

      setJobProgress({ message: "Processing with AI...", percent: 5 });

      const POLL_INTERVAL = 2000;
      const MAX_WAIT_MS = 5 * 60 * 1000; // 5 minutes
      const startTime = Date.now();

      while (Date.now() - startTime < MAX_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

        const statusResponse = await axios.get(
          `${API_CONSTANT_ONBOARDING}/data/process-excel/status/${job_id}`,
          { headers: getOnboardingHeaders() },
        );
        const statusData = statusResponse.data.data;

        if (statusData.progress) {
          setJobProgress(statusData.progress);
          if (statusData.progress.stage === "retry") {
            isRetryJob.current = true;
          }
        }

        if (statusData.status === "done") {
          const resultData = statusData.data?.data ?? statusData.data;
          const isRetry = isRetryJob.current
            || resultData?.is_failed_records_reupload
            || resultData?.is_retry
            || statusData.data?.message?.toLowerCase().includes("retry");
          if (isRetry) {
            setIsRetryUpload(true);
          }
          setAutoProcessedData(resultData);
          setStage("AUTO_REVIEW");
          setIsLoading(false);
          setIsAutoProcessing(false);
          return;
        }

        if (statusData.status === "failed") {
          throw new Error(statusData.error || "Processing job failed");
        }

      }

      throw new Error("Processing timed out. Please try again.");
    } catch (error) {
      console.log(
        "Auto-processing failed, falling back to manual mode:",
        error,
      );
    }

    setIsAutoProcessing(false);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, {
        type: "binary",
        cellDates: true,
        dateNF: "yyyy-mm-dd",
      });
      const sheetList: SheetInfo[] = wb.SheetNames.map((name) => ({
        name,
        data: XLSX.utils.sheet_to_json(wb.Sheets[name], {
          header: 1,
          raw: false,
          dateNF: "yyyy-mm-dd",
        }),
        type: null,
      }));
      setSheets(sheetList);
      setStage("IDENTIFY");
      setIsLoading(false);
    };
    reader.readAsBinaryString(file);
  };

  const handleTypeSelection = (index: number, type: SheetType) => {
    const newSheets = [...sheets];
    newSheets[index].type = type;
    setSheets(newSheets);
  };

  const proceedToHeaderCheck = () => {
    const queue = sheets.filter((s) => s.type && s.type !== "IGNORE");
    if (queue.length === 0) {
      alert("Please select at least one sheet to process.");
      return;
    }
    setProcessingQueue(queue);
    setCurrentSheetIndex(0);
    setStage("HEADER_CHECK");
  };

  const handleFinalSubmit = async () => {
    setIsLoading(true);
    setBulkOnboardProgress({ message: "Starting...", percent: 0 });
    setImportStep("saving");

    try {
      let instituteData: any = {};
      try {
        instituteData = JSON.parse(sessionStorage.getItem("_DATA_") || "{}");
      } catch (e) {
        console.error("Error parsing _DATA_", e);
      }

      const branchId = instituteData.BRANCH_ID;
      const instituteId = instituteData.INSTITUTE_ID;
      const academicYearId = instituteData.ACADEMIC_YEAR_ID;
      const databaseName = instituteData.DATABASE_NAME;

      const payload = {
        staff: [],
        students: [],
        parents: [],
        arrears: [],
      };

      if (bulkSubmissionMode === "manual") {
        payload.students = finalPayload.STUDENTS || [];
        payload.parents = finalPayload.PARENTS || [];
        payload.staff = finalPayload.STAFF || [];
        payload.arrears = finalPayload.FEE_ARREARS || [];
      } else {
        if (autoProcessedData.students && autoProcessedData.students.length > 0) {
          payload.students = autoProcessedData.students.map((s: any) => ({
            FIRST_NAME: s.FIRST_NAME,
            LAST_NAME: s.LAST_NAME,
            EMAIL_ID: s.EMAIL_ID,
            MOBILE_NUMBER: s.MOBILE_NUMBER,
            GRADE: s.GRADE,
            ADMISSION_NUMBER: s.ADMISSION_NUMBER,
            ROLL_NUMBER: s.ROLL_NUMBER,
            MIDDLE_NAME: s.MIDDLE_NAME,
            GENDER: s.GENDER,
            CATEGORY: s.CATEGORY,
            SUB_CATEGORY: s.SUB_CATEGORY,
            BLOOD_GROUP: s.BLOOD_GROUP,
            AADHAR_NUMBER: s.AADHAR_NUMBER,
            LANGUAGE: s.LANGUAGE,
            BIRTH_DATE: s.BIRTH_DATE,
            ADMISSION_DATE: s.ADMISSION_DATE,
            COUNTRY_CODE: s.COUNTRY_CODE || "+91",
            ADDRESS: s.ADDRESS,
            CITY: s.CITY,
            PINCODE: s.PINCODE,
            CLASS: s.CLASS,
            FEE_PAYMENT_DURATION_NAME: s.FEE_PAYMENT_DURATION_NAME,
            SECONDARY_LANGUAGE: s.SECONDARY_LANGUAGE,
            MOTHER_TONGUE: s.MOTHER_TONGUE,
            IDENTIFICATION_MARKS: s.IDENTIFICATION_MARKS,
            PREVIOUS_INSTITUTE_NAME: s.PREVIOUS_INSTITUTE_NAME,
            PREVIOUS_INSTITUTE_ADDRESS: s.PREVIOUS_INSTITUTE_ADDRESS,
            PREVIOUS_YEAR_OF_PASSING: s.PREVIOUS_YEAR_OF_PASSING,
            SCHOLARSHIP_ID: s.SCHOLARSHIP_ID,
            PHYSICAL_DISABILITY: s.PHYSICAL_DISABILITY,
            WHATSAPP_NUMBER: s.WHATSAPP_NUMBER,
          }));
        }

        if (autoProcessedData.parents && autoProcessedData.parents.length > 0) {
          payload.parents = autoProcessedData.parents.map((p: any) => ({
            ...p,
            RELATION: p.RELATIONSHIP || p.RELATION,
            RELATIONSHIP: undefined,
          }));
        }

        if (autoProcessedData.staff && autoProcessedData.staff.length > 0) {
          payload.staff = autoProcessedData.staff.map((s: any) => ({
            FIRST_NAME: s.FIRST_NAME,
            LAST_NAME: s.LAST_NAME,
            MIDDLE_NAME: s.MIDDLE_NAME,
            GENDER: s.GENDER,
            MOBILE_NUMBER: s.MOBILE_NUMBER,
            COUNTRY_CODE: s.COUNTRY_CODE || "+91",
            EMAIL_ID: s.EMAIL_ID,
            BIRTH_DATE: s.BIRTH_DATE,
            JOINING_DATE: s.JOINING_DATE,
            ADDRESS: s.ADDRESS,
            CITY: s.CITY,
            PINCODE: s.PINCODE,
            QUALIFICATION: s.QUALIFICATION,
            LANGUAGE: s.LANGUAGE,
            AADHAR_NUMBER: s.AADHAR_NUMBER,
            STAFF_CODE: s.STAFF_CODE,
            TEACHING_SINCE: s.TEACHING_SINCE,
            ROLES:
              typeof s.ROLES === "string"
                ? s.ROLES.split(/[, ]+/).filter(Boolean)
                : s.ROLES,
          }));
        }

        if (
          autoProcessedData.fee_arrears &&
          autoProcessedData.fee_arrears.length > 0
        ) {
          payload.arrears = autoProcessedData.fee_arrears.map((a: any) => ({
            ADMISSION_NUMBER: a.ADMISSION_NUMBER,
            MOBILE_NUMBER: a.MOBILE_NUMBER,
            AADHAR_NUMBER: a.AADHAR_NUMBER,
            AMOUNT: a.AMOUNT,
          }));
        }
      }

      const enqueueResponse = await axios.post(
        `${API_CONSTANT_ONBOARDING}/data/institute/onboard-bulk-all`,
        payload,
        {
          params: {
            branch_id: branchId,
            institute_id: instituteId,
            database_name: databaseName,
            academic_year_id: academicYearId,
          },
          headers: getOnboardingHeaders(),
        },
      );

      const { job_id } = enqueueResponse.data.data;
      if (!job_id) throw new Error("No job_id returned from server");

      const POLL_INTERVAL = 2000;
      const MAX_WAIT_MS = 10 * 60 * 1000; // 10 minutes
      const startTime = Date.now();

      while (Date.now() - startTime < MAX_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));

        const statusResponse = await axios.get(
          `${API_CONSTANT_ONBOARDING}/data/institute/onboard-bulk-all/status/${job_id}`,
          { headers: getOnboardingHeaders() },
        );
        const statusData = statusResponse.data.data;

        if (statusData.progress) {
          setBulkOnboardProgress(statusData.progress);
        }

        if (statusData.status === "done") {
          const resultData = statusData.data?.data ?? statusData.data;
          setResultModalData({ data: resultData });
          setShowConfirmModal(false);
          setStage("DONE");
          window.dispatchEvent(new Event("formUpdated"));

          const finalStoredData = {
            STUDENT_DATA: payload.students,
            PARENT_DATA: payload.parents,
            STAFF_DATA: payload.staff,
            FEE_ARREARS_DATA: payload.arrears,
          };
          sessionStorage.setItem(
            "aiManagmentHub",
            JSON.stringify(finalStoredData),
          );
          window.dispatchEvent(new Event("formUpdated"));
          return;
        }

        if (statusData.status === "failed") {
          throw new Error(statusData.error || "Bulk onboard job failed");
        }

      }

      throw new Error("Bulk onboard timed out. Please try again.");
    } catch (error: any) {
      console.error("Auto-processed bulk upload error:", error);
      setImportStep("confirm");
      setShowConfirmModal(false);
      showGlobalModal(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload data. Please try again or use manual mapping.",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (stage === "DONE" && resultModalData?.data) {
      // Check if there is actually a file to download (either URL or Base64)
      if (
        resultModalData.data.excel_file_url ||
        resultModalData.data.master_file_base64
      ) {
        console.log("Auto-downloading failed records...");
        handleDownload();
      }
    }
    // We only want this to trigger when 'stage' changes to "DONE"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);

  // const Stepper = () => {
  //     const steps = [
  //         { id: 'IDENTIFY', label: 'Identify' },
  //         { id: 'HEADER_CHECK', label: 'Clean' },
  //         { id: 'MAPPING', label: 'Map' },
  //         { id: 'REVIEW', label: 'Review' },
  //         { id: 'DONE', label: 'Done' }
  //     ];
  //     const currentIdx = steps.findIndex(s => s.id === stage);

  //     return (
  //         <div className="w-full mb-10 px-4">
  //             <div className="flex items-center justify-between relative">
  //                 <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-1/2 rounded-full -z-10" />

  //                 <motion.div
  //                     className="absolute top-1/2 left-0 h-1 bg-gradient-to-r from-blue-600 to-indigo-600 -translate-y-1/2 rounded-full -z-10"
  //                     initial={{ width: 0 }}
  //                     animate={{ width: `${(currentIdx / (steps.length - 1)) * 100}%` }}
  //                     transition={{ duration: 0.5, ease: "easeInOut" }}
  //                 />

  //                 {steps.map((s, idx) => {
  //                     const isActive = idx === currentIdx;
  //                     const isCompleted = idx < currentIdx;
  //                     const isFuture = idx > currentIdx;

  //                     return (
  //                         <div key={s.id} className="flex flex-col items-center group relative z-0">
  //                             <motion.div
  //                                 className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-4 transition-colors duration-300
  //                                     ${isActive ? 'bg-white border-blue-600 text-blue-600 shadow-xl shadow-blue-200' :
  //                                         isCompleted ? 'bg-blue-600 border-blue-600 text-white' :
  //                                             'bg-white border-gray-200 text-gray-300'}`}
  //                                 whileHover={{ scale: 1.1 }}
  //                                 transition={{ type: "spring", stiffness: 300, damping: 20 }}
  //                             >
  //                                 {isCompleted ? <Check className="w-5 h-5" /> : idx + 1}
  //                             </motion.div>
  //                             <span className={`absolute top-12 text-xs font-semibold whitespace-nowrap transition-colors duration-300
  //                                 ${isActive ? 'text-blue-700' : isCompleted ? 'text-blue-600/70' : 'text-gray-300'}`}>
  //                                 {s.label}
  //                             </span>
  //                         </div>
  //                     );
  //                 })}
  //             </div>
  //         </div>
  //     );
  // };
  // Inside AIManagmentHub
  useEffect(() => {
    sessionStorage.setItem("aiHubCurrentStage", stage);
    // This tells the parent SFR component to run 'checkCompletion()'
    window.dispatchEvent(
      new CustomEvent("formUpdated", { detail: { aiHubStage: stage } }),
    );
  }, [stage]);

  // NEW: Manage the internal steps of the confirmation modal
  const [importStep, setImportStep] = useState<
    "confirm" | "saving" | "success"
  >("confirm");

  // Reset import step when modal is reopened
  useEffect(() => {
    if (showConfirmModal) {
      setImportStep("confirm");
    }
  }, [showConfirmModal]);

  const renderUploadStage = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="w-full"
    >
      <div className="relative w-full flex flex-col">
        {/* Main centered container */}
        <div className="ai-data-upload-shell p-5 w-[60vw] m-auto flex flex-col flex-1">
          <AnimatePresence mode="wait">

              /* --- NORMAL UPLOAD STATE --- */
            <motion.div
              key="upload"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full flex flex-col"
            >

              <div
                className={`ai-data-dropzone relative group transition-all rounded-[13.2px] p-[35.2px] flex flex-col items-center justify-center mb-[5vh] overflow-hidden min-h-[28vh] 
                  `}
              >
                {isLoading ? ( // Note: You should change this 'true' to 'isAutoProcessing' in your production code
                   <motion.div
                    key="loading"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.3 }}
                    className="w-full h-full rounded-[13.2px] flex flex-col items-center justify-center py-[3vh] relative"
                  >
                    <div
                      style={{
                        width: "57.935vh",
                        maxWidth: "100%",
                        background: "#fff",
                        border: "0.126vh solid #EAECF3",
                        borderRadius: "2.267vh",
                        padding: "3.5vh 3.275vh",
                        boxShadow: "0 2.519vh 6.297vh rgba(20,20,50,.08)",
                        textAlign: "center",
                        margin: "0 auto",
                      }}
                    >
                      {/* Spinner */}
                      <div
                        style={{
                          position: "relative",
                          width: "8.312vh",
                          height: "8.312vh",
                          margin: "0 auto 2.5vh",
                        }}
                      >
                        <div
                          className="animate-[spin_0.9s_linear_infinite]"
                          style={{
                            position: "absolute",
                            inset: "0",
                            borderRadius: "50%",
                            border: "0.378vh solid #EAE7FB",
                            borderTopColor: "#6366F1", // Indigo-500 matching the image
                          }}
                        ></div>
                        <div
                          style={{
                            position: "absolute",
                            inset: "1.5vh",
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <SvgAsset11 />
                        </div>
                      </div>

                      {/* Header Text */}
                      <div
                        style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontWeight: "700",
                          fontSize: "2.141vh",
                          lineHeight: "1.4",
                          color: "#111827",
                          marginBottom: "3vh",
                        }}
                      >
                        AI is processing your data
                      </div>

                      {/* Steps List */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "1.8vh",
                          width: "fit-content",
                        }}
                      >
                        {[
                          "Detecting student fields",
                          "Recognising parent records",
                          "Mapping staff & faculty",
                          "Analysing fee structure",
                        ].map((label, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "1.2vh", // Precise gap between bullet and text
                            }}
                          >
                            <span
                              style={{
                                width: "0.9vh",
                                height: "0.9vh",
                                borderRadius: "50%",
                                background: "#9CA3AF", // Slate gray matching the text
                                flexShrink: 0,
                              }}
                            ></span>
                            <span
                              style={{
                                fontSize: "1.55vh",
                                fontWeight: "600", // SemiBold (matches image better than 700)
                                color: "#9CA3AF",
                                fontFamily: "Inter, sans-serif",
                                lineHeight: "1",
                              }}
                            >
                              {label}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Bottom File Icon Badge */}
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "5.5vh",
                          height: "5.5vh",
                          borderRadius: "1.259vh",
                          background: "#F7F8FB",
                          border: "0.126vh solid #EAECF3",
                          marginTop: "2vh",
                        }}
                      >
                        <SvgAsset12 />
                      </div>
                    </div>
                  </motion.div>
                ) : (
                <>
  <div
    className="smx"
    style={{
      flex: "1",
      minHeight: "0",
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      maxWidth: "90.68vh",
      margin: "0 auto",
      width: "100%",
    }}
  >
    <div style={{ textAlign: "center", marginBottom: "1.008vh" }}>
      <div
        style={{
          fontFamily: "'Space Grotesk',sans-serif",
          fontSize: "2.393vh",
          fontWeight: "700",
        }}
      >
        Import institution data
      </div>
      <div
        style={{
          fontSize: "1.637vh",
          color: "#8A92A6",
          marginTop: "0.504vh",
        }}
      >
        Upload your file — AI identifies and organises students, parents, staff
        and fees.
      </div>
    </div>
    
    {/* Dropzone Container */}
    <div
      className="hv6"
      style={{
        marginTop: "1.763vh",
        border: "0.252vh dashed #CBD0DE",
        borderRadius: "2.015vh",
        padding: "4.282vh",
        textAlign: "center",
        background: "#FBFBFE",
        position: "relative", // Added to contain the absolute input overlay
        opacity: disableForm || isLoading ? 0.6 : 1, // Optional: dims the dropzone if disabled
      }}
    >
      {/* Preserved Input Logic */}
      <input
        type="file"
        accept=".xlsx, .xls, .csv"
        onChange={handleFileUpload}
        disabled={disableForm || isLoading}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
      />

      <div
        style={{
          width: "6.549vh",
          height: "6.549vh",
          margin: "0 auto 1.511vh",
          borderRadius: "1.763vh",
          background: "#F0EFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <SvgAsset7  />
      </div>
      <div
        style={{
          fontWeight: "800",
          fontSize: "1.889vh",
          marginBottom: "0.378vh",
        }}
      >
        Drop your file here, or browse
      </div>
      <div
        style={{
          fontSize: "1.511vh",
          color: "#9AA2B4",
          marginBottom: "1.763vh",
        }}
      >
        Supports Excel and CSV files
      </div>
      
      {/* Preserved File Name Logic */}
      <span
        style={{
          display: "inline-block",
          padding: "1.385vh 2.771vh",
          borderRadius: "1.385vh",
          background: "linear-gradient(135deg,#4F46E5,#7A4DF0)",
          color: "#fff",
          fontSize: "1.7vh",
          fontWeight: "800",
        }}
      >
        {selectedFile ? selectedFile.name : "Browse files"}
      </span>
      
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "2.015vh",
          marginTop: "1.763vh",
          fontSize: "1.448vh",
          color: "#9AA2B4",
          fontWeight: "600",
        }}
      >
        <span>.xlsx</span>
        <span>.xls</span>
        <span>.csv</span>
      </div>
    </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: "1.511vh",
        marginTop: "1.763vh",
      }}
    >
      <div
        style={{
          border: "0.126vh solid #EAECF3",
          borderRadius: "1.637vh",
          padding: "1.763vh",
          background: "#fff",
        }}
      >
        <div
          style={{
            width: "3.778vh",
            height: "3.778vh",
            borderRadius: "1.134vh",
            background: "#0FA968",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.134vh",
          }}
        >
          <SvgAsset8 />
        </div>
        <div style={{ fontWeight: "800", fontSize: "1.637vh" }}>AI-powered</div>
        <div style={{ fontSize: "1.448vh", color: "#8A92A6" }}>
          Automatic field detection
        </div>
      </div>
      <div
        style={{
          border: "0.126vh solid #EAECF3",
          borderRadius: "1.637vh",
          padding: "1.763vh",
          background: "#fff",
        }}
      >
        <div
          style={{
            width: "3.778vh",
            height: "3.778vh",
            borderRadius: "1.134vh",
            background: "#E5484D",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.134vh",
          }}
        >
          <SvgAsset9 />
        </div>
        <div style={{ fontWeight: "800", fontSize: "1.637vh" }}>
          Multi-format
        </div>
        <div style={{ fontSize: "1.448vh", color: "#8A92A6" }}>
          Excel & CSV support
        </div>
      </div>
      <div
        style={{
          border: "0.126vh solid #EAECF3",
          borderRadius: "1.637vh",
          padding: "1.763vh",
          background: "#fff",
        }}
      >
        <div
          style={{
            width: "3.778vh",
            height: "3.778vh",
            borderRadius: "1.134vh",
            background: "#E8730C",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.134vh",
          }}
        >
          <SvgAsset10 />
        </div>
        <div style={{ fontWeight: "800", fontSize: "1.637vh" }}>
          Fast import
        </div>
        <div style={{ fontSize: "1.448vh", color: "#8A92A6" }}>
          Processed in seconds
        </div>
      </div>
    </div>
  </div>
</>
                )}
              </div>


              
            </motion.div>

          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );

  const renderIdentifyStage = () => (
    <div className="ai-stage-card-scroll flex-1 overflow-y-auto flex flex-col font-inter w-[calc(100%+4.5vw)] -ml-[2.25vw] -mt-3">
      <div id="tour-identify-header" className="  bg-[#fff] border-b border-[#CFD6DC] flex-shrink-0 px-3 pb-6 pt-2 ">
        <h2 className="text-[1.45vw] font-[500] mb-[1vh] font-inter text-[#000]">
          Import Student Data
        </h2>
        <div className="flex items-center gap-[0.50vw] text-[#000000] text-[1vw] font-inter font-[400]">
          <Sparkles className="w-[1.25vw] h-[1.25vw] text-[#374AF2]" />
          <span>
            AI scanned your file and found {sheets.length} sheets. Select what type of data each sheet contains.
          </span>
        </div>
      </div>

      <div className="px-[2vw] pt-4 flex-1 flex flex-col">
        {/* Sheet Selection Section */}
        <div id="tour-identify-sheets" className="border border-[#CFD6DC] rounded-[0.75vw] bg-[#fff] mb-[4vh] ">
          <div className="flex justify-between items-center p-[1.50vw]  pb-0 pt-[1vw]">
            <h3 className="text-[1.25vw] font-[400] font-inter text-[#000]">Select Sheet</h3>
            <span className="text-[1.125vw] font-[500] font-inter text-[#000]">
              Total sheets : {sheets.length < 10 ? `0${sheets.length}` : sheets.length}
            </span>
          </div>
          <div className="p-[1.2vw] pb-2 grid grid-cols-4 gap-[1.5vw]">
            {sheets.map((sheet, idx) => {
              const isActive = idx === activeSheetIdx;
              const isConfigured = sheet.type && sheet.type !== "IGNORE";

              return (
                <div
                  key={idx}
                  onClick={() => setActiveSheetIdx(idx)}
                  className={`rounded-[0.4vw] p-[0.8vw] flex items-center gap-[0.8vw] cursor-pointer transition-all border ${isActive
                    ? "bg-[#374AF2] text-[#fff] border-[#374AF2]"
                    : isConfigured
                      ? "border-[#374AF2] bg-[#fff] text-[#ABB7C2]"
                      : "border-[#CFD6DC] text-[#ABB7C2] hover:border-gray-300"
                    }`}
                >
                  <div
                    className={`p-[0.4vw] rounded-[0.3vw] border ${isActive
                      ? "bg-[#fff] text-[#374AF2] border-[#fff]"
                      : "bg-[#fff] text-[#ABB7C2] border-[#CFD6DC]"
                      }`}
                  >
                    <FileSpreadsheet className="w-[1.2vw] h-[1.2vw]" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <div className={`font-inter font-[400] text-[0.9vw] truncate ${isActive ? "text-[#fff]" : "text-[#ABB7C2]"}`}>
                      {sheet.name}
                    </div>
                    <div className={`text-[0.7vw] font-inter font-[400] ${isActive ? "text-[#fff]" : "text-[#ABB7C2]"}`}>
                      {(sheet.data?.length || 1) - 1} rows detected
                    </div>
                  </div>
                  {isConfigured && !isActive && (
                    <div className="w-[1vw] h-[1vw] bg-[#374AF2] rounded-full flex items-center justify-center text-[#fff] shrink-0 " >
                      <Check className="w-[0.6vw] h-[0.6vw]" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Configuration & Preview Section */}
        {sheets[activeSheetIdx] && (
          <div className="border border-[#CFD6DC] rounded-[0.75vw] bg-[#fff] mb-[1.50vh] animate-in fade-in duration-300">
            <div id="tour-identify-type-select" className="p-[1.50vw] border-b border-[#CFD6DC]">
              <h3 className="text-[2vh] pl-[0.5vw] font-inter font-[400] mb-[1vh] font-inter text-[#000]">Select Data Type</h3>
              <div className="relative w-[15vw] ">
                <select
                  value={sheets[activeSheetIdx].type || ""}
                  style={{
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                    appearance: "none",
                    backgroundImage: "none",
                  }}
                  onChange={(e) => handleTypeSelection(activeSheetIdx, e.target.value as SheetType)}
                  className="w-full border border-[#9D9D9D] rounded-[0.375vw]  pl-[0.5vw] pr-[2.50vw] py-[1vh] text-[1.5vh] text-[#000] font-inter font-[400] outline-none focus:border-[#374AF2] focus:ring-1 focus:ring-[#374AF2] appearance-none bg-[#fff] cursor-pointer font-inter"
                >
                  <option className="text-[#000]" value="" disabled>Select Data Type...</option>
                  <option value="STUDENT">Student Data Only</option>
                  <option value="PARENT">Parent Data Only</option>
                  <option value="STUDENT_PARENT">Student & Parent Data</option>
                  <option value="STAFF">Staff Data</option>
                  <option value="FEE_ARREARS">Fee Arrears</option>
                  <option value="IGNORE">Ignore / Skip</option>
                </select>
                <ChevronDown className="absolute right-[0.75vw] top-1/2 -translate-y-1/2 w-[1.25vw] h-[1.25vw] text-gray-500 pointer-events-none" />
              </div>
            </div>

            <div>
              <div className="p-[1.50vw] pb-[1vh]">
                <h3 className="text-[1.125vw] font-[500] font-inter text-[#000]">
                  Preview <span className="text-[#374AF2] font-normal">(first 3 rows)</span>
                </h3>
              </div>
              <div id="tour-identify-preview" className="overflow-x-auto ">
                <table className="w-full text-left border-collapse min-w-max h-[29vh] bg-[#fff] border-t border-gray-200">
                  <thead className="sticky top-0 bg-[#fff] z-10">
                    <tr className="border-b border-gray-200 text-[1vw] bg-[#fff]">
                      <th className="py-[2vh] px-[1.50vw] font-inter font-[400] text-[#000] border-r border-gray-200 border-b-2 border-[#CFD6DC]">
                        #
                      </th>
                      {sheets[activeSheetIdx].data?.[0]?.map((header, i) => (
                        <th
                          key={i}
                          className="py-[2vh] px-[1.50vw] font-inter font-[600] text-[#000] border-r border-gray-200 border-b-2 border-[#CFD6DC] last:border-r-0"
                        >
                          {header || `Column ${i + 1}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-[1vw] text-[#000] font-inter font-[400]">
                    {sheets[activeSheetIdx].data?.slice(1, 4).map((row, rowIdx) => (
                      <tr
                        key={rowIdx}
                        className="hover:bg-[rgba(239,246,255,1)] transition-colors"
                      >
                        <td className="py-[2vh] px-[1.50vw] text-[#000] font-inter font-[400] border-r border-b border-gray-200">
                          0{rowIdx + 1}
                        </td>
                        {row.map((cell, cellIdx) => (
                          <td
                            key={cellIdx}
                            className="py-[2vh] px-[1.50vw] border-r border-b border-gray-200 last:border-r-0"
                          >
                            {cell || "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {(!sheets[activeSheetIdx].data || sheets[activeSheetIdx].data.length <= 1) && (
                      <tr>
                        <td
                          colSpan={100}
                          className="py-[3vh] text-center text-gray-500 italic border-b border-gray-200"
                        >
                          No data rows found in this sheet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <div className="ai-wizard-actions flex justify-end gap-[1vw]">
          <button
            type="button"
            onClick={() => setStage("UPLOAD")}
            className="px-[2.50vw] py-[1.3vh] border-[0.5px] border-[#3E3E3E] rounded-[0.5vw] font-medium flex items-center justify-center gap-[0.50vw] text-[1.125vw] hover:bg-gray-50 transition-colors w-[13vw] text-[#000]"
          >
            <ArrowLeft className="w-[1.25vw] h-[1.25vw]" /> Back
          </button>
          <button
            id="tour-identify-next"
            type="button"
            onClick={proceedToHeaderCheck}
            className="w-[13vw] justify-center  py-[1.3vh] bg-[#374AF2] text-[#fff] rounded-[0.5vw] font-[400] font-inter flex items-center gap-[0.50vw] text-[2.22vh] hover:bg-[#374AF2]/90 transition-colors font-inter"
          >
            Next <ArrowRight className="w-[1.25vw] h-[1.25vw]" />
          </button>
        </div>
      </div>
    </div>
  );

  const emptyData = { CERTIFICATE_LIST: [], STUDENT_DATA: [] };
  const retryStats = resultModalData?.data?.stats;
  const persistedCompletionData = (() => {
    try {
      return JSON.parse(sessionStorage.getItem("aiManagmentHub") || "{}");
    } catch {
      return {};
    }
  })();

  const countStudents = isRetryUpload
    ? (retryStats?.students?.success ?? retryStats?.students?.total ?? 0)
    : bulkSubmissionMode === "manual"
      ? finalPayload.STUDENTS.length || 0
      : autoProcessedData?.students?.length ||
        persistedCompletionData?.STUDENT_DATA?.length ||
        0;
  const countParents = isRetryUpload
    ? (retryStats?.parents?.success ?? retryStats?.parents?.total ?? 0)
    : bulkSubmissionMode === "manual"
      ? finalPayload.PARENTS.length || 0
      : autoProcessedData?.parents?.length ||
        persistedCompletionData?.PARENT_DATA?.length ||
        0;
  const countStaff = isRetryUpload
    ? (retryStats?.staff?.success ?? retryStats?.staff?.total ?? 0)
    : bulkSubmissionMode === "manual"
      ? finalPayload.STAFF.length || 0
      : autoProcessedData?.staff?.length ||
        persistedCompletionData?.STAFF_DATA?.length ||
        0;
  const countArrears = isRetryUpload
    ? (retryStats?.arrears?.success ?? retryStats?.arrears?.total ?? 0)
    : bulkSubmissionMode === "manual"
      ? finalPayload.FEE_ARREARS.length || 0
      : autoProcessedData?.fee_arrears?.length ||
        persistedCompletionData?.FEE_ARREARS_DATA?.length ||
        0;
  const totalProcessedRecords =
    countStudents + countParents + countStaff + countArrears;

  return (
    <Formik
      initialValues={{
        ...(Boolean(JSON.parse(sessionStorage.getItem("aiManagmentHub")))
          ? JSON.parse(sessionStorage.getItem("aiManagmentHub")) || {}
          : emptyData),
      }}
      enableReinitialize
      onSubmit={async (values) => {

        sessionStorage.setItem("aiManagmentHub", JSON.stringify(values));
      }}
    >
      {({ values, handleSubmit, resetForm, setFieldValue }) => (
        <FormWrapper
          customClass="ai-management-hub-form"
          title={
            stage === "UPLOAD" ? (
              <>
                <div className="flex flex-col items-start text-left gap-[clamp(0.125rem,0.5vw,0.25rem)] w-full ml-[0px]" style={{
                  width: "calc(100% + 4.5vw)",
                  marginLeft: "-1.25vw",
                  marginTop: "-2vh"
                }}>
                  <span className="font-inter font-[500] text-[#000] text-[clamp(0.875rem,1.5vw,1.25rem)]">
                    Import Institution Data
                  </span>
                  <span className="font-inter font-[400] text-[#000] leading-relaxed text-[clamp(0.625rem,1vw,0.875rem)] mb-2">
                    Upload your data file and let AI automatically identify and
                    organize students, parents, staff, and institutional data.{" "}
                    <br />

                    You can also map fields manually if needed.
                  </span>

                  <div
                    style={{
                      width: "calc(100% + 4.5vw)",
                      marginLeft: "-2.25vw",
                    }}
                  >
                    <hr className="w-full border-t border-[#CFD6DC]" />
                  </div>
                </div>
              </>
            ) : null // <--
          }
          childern={
            <Form onSubmit={handleSubmit} className="relative ">
              <div className=" mx-auto w-full   ">
                {/* {stage !== 'UPLOAD' && stage !== 'AUTO_REVIEW' && <Stepper />} */}

                <div className="w-full ">
                  {stage === "UPLOAD" && renderUploadStage()}

                 {stage === "AUTO_REVIEW" && autoProcessedData && (
  (() => {
    // 1. Calculate total records
    const totalRecords =
      (autoProcessedData.counts?.students || autoProcessedData.students?.length || 0) +
      (autoProcessedData.counts?.parents || autoProcessedData.parents?.length || 0) +
      (autoProcessedData.counts?.staff || autoProcessedData.staff?.length || 0) +
      (autoProcessedData.counts?.fee_arrears || autoProcessedData.fee_arrears?.length || 0);

    // 2. Setup dynamic data for the table
    const currentData = autoProcessedData[activeTab] || [];
    const columns = currentData.length > 0 ? Object.keys(currentData[0]) : [];
    
    // Use the 4-column layout from your design if there are 4 columns, otherwise divide evenly
    const gridTemplateColumns = columns.length === 4 
      ? "1fr 1.4fr .8fr 1.2fr" 
      : `repeat(${columns.length}, 1fr)`;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="smx ai-extraction-review"
        style={{
          flex: "1",
          minHeight: "0",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "1.511vh",
          width: "100%"
        }}
      >
        {/* TOP BANNER */}
        <div className="ai-extraction-success"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1.385vh",
            padding: "1.637vh 1.889vh",
            borderRadius: "1.637vh",
            background: "#F0FBF5",
            border: "0.126vh solid #C4EED6",
          }}
        >
          <div
            style={{
              width: "4.282vh",
              height: "4.282vh",
              borderRadius: "1.259vh",
              background: "#0FA968",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "none",
            }}
          >
            <SvgAsset13 />
          </div>
          <div>
            <div
              style={{
                fontWeight: "800",
                fontSize: "1.763vh",
                color: "#0B6B44",
                fontFamily: "Inter, sans-serif"
              }}
            >
              {totalRecords} records successfully extracted
            </div>
            <div style={{ fontSize: "1.511vh", color: "#3B8A66", fontFamily: "Inter, sans-serif" }}>
              Review and edit the extracted data before continuing.
            </div>
          </div>
        </div>

        {/* MAIN CONTAINER */}
        <div
          style={{
            flex: "1",
            minHeight: "0",
            border: "0.126vh solid #EAECF3",
            borderRadius: "1.889vh",
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* TABS HEADER */}
          <div
            style={{
              display: "flex",
              gap: "2.267vh",
              padding: "1.511vh 2.267vh 0",
              borderBottom: "0.126vh solid #EEF0F6",
            }}
          >
            {[
              {
                id: "students",
                label: "Students",
                count: autoProcessedData.counts?.students || autoProcessedData.students?.length || 0,
              },
              {
                id: "parents",
                label: "Parent",
                count: autoProcessedData.counts?.parents || autoProcessedData.parents?.length || 0,
              },
              {
                id: "staff",
                label: "Staff",
                count: autoProcessedData.counts?.staff || autoProcessedData.staff?.length || 0,
              },
              {
                id: "fee_arrears",
                label: "Fee Arrears",
                count: autoProcessedData.counts?.fee_arrears || autoProcessedData.fee_arrears?.length || 0,
              },
            ].map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <div
      key={tab.id}
      onClick={() => setActiveTab(tab.id)}
      role="button"       /* Tells screen readers this acts like a button */
      tabIndex={0}        /* Makes it focusable with the keyboard */
      className="outline-none" /* Prevents default focus rings */
      style={{
        display: "inline-block",
        background: "none",
        cursor: "pointer",
        fontFamily: "Inter, sans-serif",
        fontSize: "1.511vh",
        fontWeight: isActive ? "800" : "600",
        color: isActive ? "#4F46E5" : "#9AA2B4",
        paddingBottom: "1.2vh",
        borderBottom: isActive ? "0.25vh solid #4F46E5" : "0.25vh solid transparent",
        transition: "all 0.2s ease"
      }}
    >
      {tab.label} ({tab.count})
    </div>
              );
            })}
          </div>

          {/* TABLE DATA AREA */}
          <div
            className="smx [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            style={{ flex: "1", minHeight: "0", overflowY: "auto" }}
          >
            {/* GRID HEADER */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: gridTemplateColumns,
                gap: "0",
                fontSize: "1.322vh",
                fontWeight: "800",
                letterSpacing: ".05em",
                color: "#9AA2B4",
                padding: "1.385vh 2.267vh",
                borderBottom: "0.126vh solid #EEF0F6",
                background: "#FAFBFD",
                textTransform: "uppercase",
                fontFamily: "Inter, sans-serif"
              }}
            >
              {columns.length > 0 ? (
                columns.map((col) => <span key={col}>{col.replace(/_/g, " ")}</span>)
              ) : (
                <span>NO DATA AVAILABLE</span>
              )}
            </div>

            {/* GRID ROWS */}
            {currentData.slice(0, 100).map((record, $index) => (
              <React.Fragment key={$index}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: gridTemplateColumns,
                    gap: "1.5vh", // Added small gap to prevent text overlap
                    fontSize: "1.574vh",
                    padding: "1.385vh 2.267vh",
                    borderBottom: "0.126vh solid #F3F4F8",
                    alignItems: "center",
                    fontFamily: "Inter, sans-serif"
                  }}
                >
                  {columns.map((col, colIdx) => (
                    <span 
                      key={col}
                      style={{
                        // Matches target design colors: Col 1 is Purple, Col 2 is Dark, Rest are Gray
                        fontWeight: (colIdx === 1 ? "600" : "400"),
                        color: (colIdx === 1 ? "#20242E" : "#5A6173"),
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}
                    >
                      {record[col] || "-"}
                    </span>
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
          
          {/* LIMIT INDICATOR */}
          {currentData.length > 100 && (
            <div style={{ padding: "1.5vh 2.267vh", background: "#FAFBFD", fontSize: "1.322vh", textAlign: "center", color: "#9AA2B4", borderTop: "0.126vh solid #EEF0F6", fontFamily: "Inter, sans-serif" }}>
              Showing first 100 rows
            </div>
          )}
        </div>

        {/* ACTION BUTTONS */}
        <div className="ai-wizard-actions" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.5vh" }}>
          <button
            type="button"
            onClick={() => {
              setAutoProcessedData(null);
              setStage("UPLOAD");
            }}
            className="flex items-center justify-center gap-2 transition-all duration-300"
            style={{
              background: "#fff",
              width: "14vw", // Kept your original action button sizings
              padding: "1vh 0",
              borderRadius: "0.8vh",
              fontSize: "1.5vh",
              fontFamily: "Inter, sans-serif",
              color: "#000",
              border: "0.1vh solid #3E3E3E",
              cursor: "pointer"
            }}
          >
            <ArrowLeft style={{ width: "1.6vh", height: "1.6vh" }} strokeWidth={2.5} />
            Back
          </button>

          <div style={{ display: "flex", gap: "1.5vh" }}>
            <button
              type="button"
              onClick={async () => {
                if (uploadedFile) {
                  parseFileForManualMode(uploadedFile);
                } else {
                  setStage("UPLOAD");
                }
              }}
              className="flex items-center justify-center transition-all duration-300"
              style={{
                background: "#fff",
                width: "14vw",
                padding: "1vh 0",
                borderRadius: "0.8vh",
                fontSize: "1.5vh",
                fontFamily: "Inter, sans-serif",
                color: "#374151",
                border: "0.1vh solid #3E3E3E",
                cursor: "pointer"
              }}
            >
              Manual Mapping
            </button>
            <button
              type="button"
              onClick={() => {
                setBulkSubmissionMode("auto");
                setShowConfirmModal(true);
              }}
              className="flex items-center justify-center gap-2 transition-all duration-300"
              style={{
                background: "#374AF2",
                color: "#fff",
                width: "14vw",
                padding: "1vh 0",
                borderRadius: "0.8vh",
                fontSize: "1.5vh",
                fontFamily: "Inter, sans-serif",
                border: "none",
                cursor: "pointer"
              }}
            >
              Proceed
            </button>
          </div>
        </div>
      </motion.div>
    );
  })()
)}

                  {stage === "IDENTIFY" && renderIdentifyStage()}

                  {stage === "HEADER_CHECK" && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="ai-stage-card-scroll flex-1 flex flex-col overflow-hidden font-inter w-[calc(100%+4.5vw)] -mx-[2.25vw] -mt-2"
                    >
                      <div id="tour-header-title" className="px-3 pb-3 border-b border-[#CFD6DC] bg-[#fff] flex-shrink-0 font-inter">
                        <div className="flex items-center gap-[1vw] mb-[1vh] font-inter">
                          <h2 className="text-[1.45vw] font-[500] font-inter text-[#000]">Select Headers:</h2>
                          <button className="px-[2vw] py-[1.5vh] border border-[#374AF2] text-[#374AF2] rounded-[0.375vw] font-inter font-[500] bg-[#fff] text-[1.125vw] ">
                            {processingQueue[currentSheetIndex]?.name}
                          </button>
                        </div>
                        <p className="text-[1vw] text-[#000] font-inter font-[400] mt-2">
                          Data Type :{" "}
                          <span className="font-[600] text-[#374AF2]">
                            {{
                              STUDENT: "Student Data",
                              PARENT: "Parent Data",
                              STUDENT_PARENT: "Student & Parent Data",
                              STAFF: "Staff Data",
                              FEE_ARREARS: "Fee Arrears Data"
                            }[processingQueue[currentSheetIndex]?.type as string] || "Not Selected"}
                          </span>
                        </p>
                      </div>

                      <div className="px-[2.25vw] pb-[2vw] pt-[3vh] flex-1 flex flex-col overflow-hidden font-inter">

                        <div id="tour-header-table" className="border border-[#CFD6DC] rounded-[0.75vw] bg-[#fff] flex-shrink-0 flex flex-col overflow-hidden font-inter">
                          <div className="p-[1.50vw] text-center font-inter  bg-[#fff]">
                            <h3 className="text-[1.5vw] font-medium text-[#374AF2] font-inter font-[500]">
                              {{
                                STUDENT: "Student Data",
                                PARENT: "Parent Data",
                                STUDENT_PARENT: "Student & Parent Data",
                                STAFF: "Staff Data",
                                FEE_ARREARS: "Fee Arrears Data"
                              }[processingQueue[currentSheetIndex]?.type as string] || "Sheet Data"}
                            </h3>
                          </div>

                          <div className="overflow-auto h-[60vh] font-inter">
                            <table className="w-full text-left border-collapse min-w-max relative font-inter">
                              <thead className="sticky top-0 bg-[#fff] z-20 font-inter">
                                <tr className="bg-[#fff] text-[1.125vw] font-inter">

                                  <th className="py-[2vh] px-[1.50vw] font-[500] font-inter text-[#000] border-r border-gray-200 shadow-[inset_0_-2px_0_0_#9CA3AF,inset_0_1px_0_0_#E5E7EB]">
                                    #
                                  </th>

                                  {processingQueue[currentSheetIndex]?.data?.[0]
                                    ?.slice(0, 8)
                                    .map((headerText: any, i: number) => (
                                      <th
                                        key={i}
                                        className="py-[1vh] px-[1.50vw] font-inter font-[500] text-[#000] border-r border-gray-200 shadow-[inset_0_-2px_0_0_#9CA3AF,inset_0_1px_0_0_#E5E7EB] last:border-r-0"
                                      >
                                        {headerText || `Column ${i + 1}`}
                                      </th>
                                    ))}
                                </tr>
                              </thead>
                              <tbody id="tour-header-rows" className="text-[1vw] text-[#000] font-inter">
                                {processingQueue[currentSheetIndex]?.data
                                  .slice(0)
                                  .map((row: any[], loopIndex: number) => {
                                    const rowIndex = loopIndex;
                                    const id = (rowIndex + 1).toString().padStart(2, '0');

                                    return (
                                      <tr
                                        key={id}
                                        onClick={() => {
                                          const newQueue = [...processingQueue];
                                          newQueue[currentSheetIndex] = {
                                            ...newQueue[currentSheetIndex],
                                          };

                                          newQueue[currentSheetIndex].data = newQueue[currentSheetIndex].data.slice(rowIndex);
                                          setProcessingQueue(newQueue);

                                          const sheetType = newQueue[currentSheetIndex].type;
                                          const existingMapping = sheetType ? savedMappings[sheetType] : null;

                                          if (existingMapping) {
                                            const rawData = newQueue[currentSheetIndex].data.slice(1);
                                            const headers = newQueue[currentSheetIndex].data[0];

                                            const transformed = transformSheetData(
                                              headers,
                                              rawData,
                                              sheetType,
                                              existingMapping.mapping,
                                              existingMapping.fixedValues
                                            );

                                            const finalMappedData = sheetType === "PARENT" ? transformed.flat() : transformed;
                                            setMappedData(finalMappedData);
                                            setStage("REVIEW");
                                          } else {
                                            setStage("MAPPING");
                                          }
                                        }}
                                        className="relative group cursor-pointer hover:bg-[#486B381A]  last:border-0 font-inter transition-colors"
                                      >
                                        <td className="py-[2vh] px-[1.50vw] font-inter text-[#000] border-r border-b border-gray-200">
                                          {id}</td>

                                        {row.slice(0, 8).map((cell: any, cellIdx: number) => (
                                          <td key={cellIdx} className="py-[1vh] px-[1.50vw] font-inter font-[400] border-r border-b border-gray-200 last:border-r-0"
                                          >  <span className="line-clamp-1">{cell || "-"}</span>
                                          </td>
                                        ))}

                                        <td className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-[100%] hidden group-hover:flex items-center justify-center pointer-events-none font-inter z-20">
                                          <div className="bg-[#fff] flex flex-col items-center font-inter">
                                            <span className="text-[#374AF2] font-inter font-[500] text-[1.125vw] px-[1.5vw] pt-[0.5vh] pb-[0.5vh] font-inter">Use this row as header</span>
                                            <div className="w-full h-[3px] bg-[#374AF2] rounded-full"></div>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>


                        </div>

                        <div className="ai-wizard-actions flex justify-end gap-[1vw] mt-[1vh]">
                          <button
                            type="button"
                            onClick={() => {
                              if (currentSheetIndex === 0) {
                                setStage("IDENTIFY");
                              } else {
                                setCurrentSheetIndex(currentSheetIndex - 1);
                                setStage("REVIEW");
                              }
                            }}
                            className="px-[2.50vw] py-[1.3vh] border-[0.5px] border-[#3E3E3E] rounded-[0.5vw] font-medium flex items-center justify-center gap-[0.50vw] text-[1.125vw] hover:bg-gray-50 transition-colors w-[13vw] text-[#000]"
                          >
                            <ArrowLeft className="w-[1.25vw] h-[1.25vw] font-inter" /> Back
                          </button>

                          <button
                            type="button"
                            disabled
                            className="w-[13vw] justify-center py-[1.3vh] bg-[#374AF2] text-[#fff] rounded-[0.5vw] font-medium flex items-center gap-[0.50vw] text-[1.125vw] cursor-not-allowed font-inter"
                          >
                            Next <ArrowRight className="w-[1.25vw] h-[1.25vw] font-inter" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {stage === "MAPPING" && (
                    <div className="ai-stage-card-scroll w-full animate-in fade-in slide-in-from-right-4 duration-500 w-[calc(100%+4.5vw)] -ml-[2.25vw] -mt-2">
                      <ColumnMapper
                        sheetName={processingQueue[currentSheetIndex]?.name}
                        currentSheetIndex={currentSheetIndex}
                        processingQueue={processingQueue}
                        initialMapping={
                          processingQueue[currentSheetIndex]?.type &&
                          savedMappings[
                            processingQueue[currentSheetIndex]?.type!
                          ]?.mapping
                        }
                        initialFixedValues={
                          processingQueue[currentSheetIndex]?.type &&
                          savedMappings[
                            processingQueue[currentSheetIndex]?.type!
                          ]?.fixedValues
                        }
                        autoApplied={
                          !!(
                            processingQueue[currentSheetIndex]?.type &&
                            savedMappings[
                            processingQueue[currentSheetIndex]?.type!
                            ]
                          )
                        }
                        systemColumns={unifiedColumns}
                        csvHeaders={
                          processingQueue[currentSheetIndex]?.data[0] || []
                        }
                        sheetType={processingQueue[currentSheetIndex]?.type}
                        onCancel={() => {
                          const originalSheet = sheets.find(
                            (s) =>
                              s.name ===
                              processingQueue[currentSheetIndex].name,
                          );
                          if (originalSheet) {
                            const newQueue = [...processingQueue];
                            newQueue[currentSheetIndex] = {
                              ...newQueue[currentSheetIndex],
                              data: originalSheet.data,
                            };
                            setProcessingQueue(newQueue);
                          }
                          setStage("HEADER_CHECK");
                        }}
                        onMappingComplete={(mapping, fixedValues) => {
                          const currentSheet = processingQueue[currentSheetIndex];
                          const rawData = currentSheet.data.slice(1);
                          const headers = currentSheet.data[0];

                          console.log("[Manual mapping completed]", {
                            sheetIndex: currentSheetIndex,
                            sheetName: currentSheet?.name,
                            sheetType: currentSheet?.type,
                            mapping,
                          });

                          const finishMapping = () => {
                            const transformed = transformSheetData(
                              headers,
                              rawData,
                              currentSheet.type,
                              mapping,
                              fixedValues
                            );
                            const finalMappedData =
                              currentSheet.type === "PARENT" ? transformed.flat() : transformed;
                            setMappedData(finalMappedData);
                            setStage("REVIEW");
                          };

                          const remainingSameType = processingQueue
                            .slice(currentSheetIndex + 1)
                            .filter((s) => s.type === currentSheet.type).length;

                          if (currentSheet.type && !savedMappings[currentSheet.type] && remainingSameType > 0) {
                            showGlobalModal(
                              `Do you want to apply this mapping to all pending ${currentSheet.type === "STUDENT" ? "Student Data" : currentSheet.type
                              } sheets?`,
                              "confirm",
                              {
                                showConfirm: true,
                                onConfirm: () => {
                                  setSavedMappings((prev) => ({
                                    ...prev,
                                    [currentSheet.type!]: { mapping, fixedValues },
                                  }));
                                  finishMapping();
                                },
                                onCancel: () => {
                                  finishMapping();
                                },
                                confimLabel: "Yes, use for all",
                                cancelLabel: "No, map individually",
                              }
                            );
                          } else {
                            finishMapping();
                          }
                        }}
                      />
                    </div>
                  )}

                  {stage === "REVIEW" && (
                    <div className="ai-stage-card-scroll w-full animate-in fade-in zoom-in-95 duration-500 w-[calc(100%+4.5vw)] -ml-[2.25vw] -mt-5">
                      <DataReview
                        data={mappedData}
                        schema={
                          processingQueue[currentSheetIndex]?.type === "STAFF"
                            ? staffSchema
                            : processingQueue[currentSheetIndex]?.type ===
                              "FEE_ARREARS"
                              ? feeArrearsSchema
                              : processingQueue[currentSheetIndex]?.type ===
                                "PARENT"
                                ? parentSchema
                                : studentSchema
                        }
                        onCancel={() => setStage("MAPPING")}
                        onComplete={(validData) => {
                          const currentSheet =
                            processingQueue[currentSheetIndex];

                          // 1. Save THIS sheet's valid data into our dictionary using its index
                          const updatedSheetData = {
                            ...sheetValidData,
                            [currentSheetIndex]: validData,
                          };
                          setSheetValidData(updatedSheetData);

                          // 2. Rebuild the ENTIRE final payload from scratch to prevent duplicates
                          const newPayload = {
                            STUDENTS: [],
                            PARENTS: [],
                            STAFF: [],
                            FEE_ARREARS: [],
                          };

                          processingQueue.forEach((sheet, idx) => {
                            const sheetData = updatedSheetData[idx];
                            if (!sheetData) return; // Skip if this sheet hasn't been reviewed yet

                            if (
                              sheet.type === "STUDENT" ||
                              sheet.type === "STUDENT_PARENT"
                            ) {
                              const students = sheetData.map(
                                (d: any) => d.student,
                              );
                              const parents = sheetData.flatMap(
                                (d: any) => d.parents,
                              );

                              if (sheet.type === "STUDENT") {
                                newPayload.STUDENTS = [
                                  ...newPayload.STUDENTS,
                                  ...students,
                                ];
                              } else {
                                newPayload.STUDENTS = [
                                  ...newPayload.STUDENTS,
                                  ...students,
                                ];
                                newPayload.PARENTS = [
                                  ...newPayload.PARENTS,
                                  ...parents,
                                ];
                              }
                            } else if (sheet.type === "PARENT") {
                              newPayload.PARENTS = [
                                ...newPayload.PARENTS,
                                ...sheetData,
                              ];
                            } else if (sheet.type === "STAFF") {
                              const staffList = sheetData.map((d: any) => ({
                                ...d,
                                ROLES: d.ROLES || [],
                              }));
                              newPayload.STAFF = [
                                ...newPayload.STAFF,
                                ...staffList,
                              ];
                            } else if (sheet.type === "FEE_ARREARS") {
                              newPayload.FEE_ARREARS = [
                                ...newPayload.FEE_ARREARS,
                                ...sheetData,
                              ];
                            }
                          });

                          // 3. Update the clean payload
                          setFinalPayload(newPayload);

                          // 4. Move to next sheet OR finish
                          if (currentSheetIndex < processingQueue.length - 1) {
                            setCurrentSheetIndex(currentSheetIndex + 1);
                            setStage("HEADER_CHECK");
                          } else {
                            // 1. Update Formik State
                            setFieldValue("STUDENT_DATA", newPayload.STUDENTS);
                            setFieldValue("PARENT_DATA", newPayload.PARENTS);
                            setFieldValue("STAFF_DATA", newPayload.STAFF);
                            setFieldValue(
                              "FEE_ARREARS_DATA",
                              newPayload.FEE_ARREARS,
                            );


                            setBulkSubmissionMode("manual");
                            setShowConfirmModal(true);
                          }
                        }}
                      />
                    </div>
                  )}

                 {stage === "DONE" ? (
  <>
    <div
      className="smx"
      style={{
        flex: "1",
        minHeight: "0",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center", // Centered the whole block vertically
        padding: "4vh 2vw",
      }}
    >
      {/* Main White Content Wrapper */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          border: "1px solid #EAECF3",
          padding: "5vh 3vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          maxWidth: "57vw", // Matching your original container width
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.03)",
        }}
      >
        {/* Main Success Checkmark */}
        <div
          style={{
            width: "7.053vh",
            height: "7.053vh",
            borderRadius: "50%",
            background: "linear-gradient(135deg,#0FA968,#34D399)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "1.763vh",
            animation: "popIn .5s ease both",
            boxShadow: "0 1.511vh 3.778vh rgba(15,169,104,.35)",
          }}
        >
          <Check style={{ color: "#fff", width: "3.5vh", height: "3.5vh" }} strokeWidth={2.75} />
        </div>

        <div
          style={{
            fontFamily: "'Space Grotesk',sans-serif",
            fontSize: "2.771vh",
            fontWeight: "700",
            marginBottom: "0.63vh",
            color: "#111827",
          }}
        >
          Import completed
        </div>

        <div
          style={{
            fontSize: "1.7vh",
            color: "#8A92A6",
            textAlign: "center",
            marginBottom: "3.5vh", // Slightly increased to give space before cards
            maxWidth: "55.416vh",
          }}
        >
          Your data has been successfully imported and validated. Your
          institution is now fully live on Soomuch.AI.
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "1.763vh",
            width: "100%",
            maxWidth: "80.605vh",
          }}
        >
          {/* Students Card */}
          <div
            style={{
              border: "0.126vh solid #EAECF3",
              borderRadius: "1.763vh",
              padding: "2.015vh",
              textAlign: "center",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                width: "4.282vh",
                height: "4.282vh",
                borderRadius: "1.259vh",
                background: "#F0EFFF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.134vh",
              }}
            >
              <svg
                style={{ color: "#a855f7", width: "2.2vh", height: "2.2vh" }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 4L21.5 8.5L12 13L2.5 8.5Z" />
                <path d="M2.5 8.5V16" />
                <circle cx="12" cy="14.5" r="3.75" />
                <path d="M4.5 22C4.5 19 7.5 17.5 12 17.5C16.5 17.5 19.5 19 19.5 22" />
              </svg>
            </div>
            <div
              style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: "2.771vh",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              {countStudents}
            </div>
            <div style={{ fontSize: "1.448vh", color: "#8A92A6" }}>
              Students imported
            </div>
            <div
              style={{
                fontSize: "1.385vh",
                color: "#0E9F6E",
                fontWeight: "700",
                marginTop: "0.63vh",
              }}
            >
              ✓ All validated
            </div>
          </div>

          {/* Staff Card */}
          <div
            style={{
              border: "0.126vh solid #EAECF3",
              borderRadius: "1.763vh",
              padding: "2.015vh",
              textAlign: "center",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                width: "4.282vh",
                height: "4.282vh",
                borderRadius: "1.259vh",
                background: "#F0FBF5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.134vh",
              }}
            >
              <Users style={{ color: "#10b981", width: "2.2vh", height: "2.2vh" }} />
            </div>
            <div
              style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: "2.771vh",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              {countStaff}
            </div>
            <div style={{ fontSize: "1.448vh", color: "#8A92A6" }}>
              Staff imported
            </div>
            <div
              style={{
                fontSize: "1.385vh",
                color: "#0E9F6E",
                fontWeight: "700",
                marginTop: "0.63vh",
              }}
            >
              ✓ All validated
            </div>
          </div>

          {/* Fee Records Card */}
          <div
            style={{
              border: "0.126vh solid #EAECF3",
              borderRadius: "1.763vh",
              padding: "2.015vh",
              textAlign: "center",
              background: "#ffffff",
            }}
          >
            <div
              style={{
                width: "4.282vh",
                height: "4.282vh",
                borderRadius: "1.259vh",
                background: "#FFF6E9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 1.134vh",
              }}
            >
              <IndianRupee style={{ color: "#f59e0b", width: "2.2vh", height: "2.2vh" }} />
            </div>
            <div
              style={{
                fontFamily: "'Space Grotesk',sans-serif",
                fontSize: "2.771vh",
                fontWeight: "700",
                color: "#111827",
              }}
            >
              {countArrears}
            </div>
            <div style={{ fontSize: "1.448vh", color: "#8A92A6" }}>
              Fee records processed
            </div>
            <div
              style={{
                fontSize: "1.385vh",
                color: "#0E9F6E",
                fontWeight: "700",
                marginTop: "0.63vh",
              }}
            >
              ✓ All validated
            </div>
          </div>
        </div>
      </div>
    </div>
  </>
) : null}
                </div>
              </div>
              {/* NEW MODAL DESIGN */}
              <AnimatePresence>
                {showConfirmModal && (
                  <div className="fixed top-0 bottom-0 left-0 right-0 z-50 flex items-center justify-center pl-[18vw] p-4 font-sans">
                    {/* Backdrop */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => !isLoading && setShowConfirmModal(false)}
                      className="absolute inset-0 bg-[#000]/[0.45]"
                    />
                    {/* Modal Container */}
<motion.div
  layout
  initial={{ opacity: 0, scale: 0.95, y: 10 }}
  animate={{ opacity: 1, scale: 1, y: 0 }}
  exit={{ opacity: 0, scale: 0.95, y: 10 }}
  transition={{ type: "spring", duration: 0.4, bounce: 0.15 }}
  className="relative z-10 flex flex-col items-center justify-center"
>


                      <AnimatePresence mode="wait">
                        {/* STEP 1: CONFIRMATION  */}
                        {importStep === "confirm" && (
                          <motion.div
                            key="confirm"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="w-full flex flex-col font-sans p-[2.2vh] pb-[2.5vh]"
                            style={{ backgroundColor: '#ffffff' }}
                          >
                            {/* Header row */}
                            <div className="flex items-center gap-[1.2vh] mb-[2vh]">
                              <div className="w-[4.8vh] h-[4.8vh] rounded-[1.2vh] bg-indigo-50 flex items-center justify-center shrink-0">
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#4F46E5"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="w-[2.4vh] h-[2.4vh]"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                              </div>
                              <div>
                                <h1 className="text-[2vh] font-inter font-[500] text-gray-900 leading-tight">
                                  Confirm data import
                                </h1>
                                <p className="text-[1.5vh] text-gray-400 font-inter font-[400]">
                                  Importing into your school management system
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-4 gap-[1.2vh] mb-[2vh]">
                              {[
                                {
                                  label: "Students",
                                  count: countStudents,
                                  icon: (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[2.2vh] h-[2.2vh]">
                                      <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                                      <path d="M6 12v5c3 3 9 3 12 0v-5" />
                                    </svg>
                                  ),
                                },
                                {
                                  label: "Parents",
                                  count: countParents,
                                  icon: (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[2.2vh] h-[2.2vh]">
                                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                                      <circle cx="9" cy="7" r="4" />
                                      <path d="M23 21v-2a4 4 0 00-3-3.87" />
                                      <path d="M16 3.13a4 4 0 010 7.75" />
                                    </svg>
                                  ),
                                },
                                {
                                  label: "Staff",
                                  count: countStaff,
                                  icon: (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[2.2vh] h-[2.2vh]">
                                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                                      <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                                    </svg>
                                  ),
                                },
                                {
                                  label: "Fee arrears",
                                  count: countArrears,
                                  icon: (
                                    <svg viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-[2.2vh] h-[2.2vh]">
                                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                                      <line x1="1" y1="10" x2="23" y2="10" />
                                    </svg>
                                  ),
                                },
                              ].map(({ label, count, icon }) => (
                                <div
                                  key={label}
                                  className="bg-indigo-50 rounded-[1.2vh] flex flex-col items-center justify-center py-[1.4vh] px-[1vh] gap-[0.5vh]"
                                >
                                  {icon}
                                  <span className="text-[2.4vh] font-inter font-[600] text-indigo-800 leading-none">
                                    {count}
                                  </span>
                                  <span className="text-[1.3vh] font-inter font-[400] text-indigo-400">
                                    {label}
                                  </span>
                                </div>
                              ))}
                            </div>

                            <div className="flex items-start gap-[1vh] bg-amber-50 border border-amber-200 rounded-[1.2vh] px-[1.5vh] py-[1.3vh] mb-[2.2vh]">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#D97706"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="w-[2.2vh] h-[2.2vh] shrink-0 mt-[0.1vh]"
                              >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                              </svg>
                              <p className="text-[1.5vh] text-amber-800 font-inter font-[400] leading-snug">
                                This action cannot be undone. Duplicate records may be created if
                                the same data was previously imported.
                              </p>
                            </div>

                            <div className="flex items-center gap-[1.2vh]">
                              <button
                                type="button"
                                onClick={() => setShowConfirmModal(false)}
                                disabled={isLoading}
                                className="px-[2.4vh] py-[1.2vh] rounded-[1vh] bg-white border border-gray-300 text-[1.5vh] font-inter font-[500] text-gray-500 hover:bg-gray-50 transition-colors disabled:opacity-50"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleFinalSubmit}
                                disabled={isLoading}
                                className="flex-1 py-[1.2vh] rounded-[1vh] bg-indigo-600 text-[1.5vh] font-inter font-[600] text-white hover:bg-indigo-700 transition-colors disabled:opacity-70"
                                style={{ color: '#ffffff' }}
                              >
                                Import {totalProcessedRecords} records
                              </button>
                            </div>
                          </motion.div>
                        )}
                        {importStep === "saving" && (
                          <React.Fragment>
            <div
              style={{
                flex: "1",
                minHeight: "0",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  width: "60.453vh",
                  maxWidth: "100%",
                  background: "#fff",
                  border: "0.126vh solid #EAECF3",
                  borderRadius: "2.267vh",
                  padding: "3.275vh",
                  boxShadow: "0 2.519vh 6.297vh rgba(20,20,50,.08)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "1.259vh",
                    marginBottom: "2.267vh",
                  }}
                >
                  <span
                    style={{
                      width: "1.637vh",
                      height: "1.637vh",
                      border: "0.252vh solid #EAE7FB",
                      borderTopColor: "#4F46E5",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "spin360 .8s linear infinite",
                    }}
                  ></span>
                  <div>
                    <div
                      style={{
                        fontFamily: "'Space Grotesk',sans-serif",
                        fontWeight: "700",
                        fontSize: "2.015vh",
                      }}
                    >
                      Saving to your management system
                    </div>
                    <div style={{ fontSize: "1.511vh", color: "#8A92A6" }}>
                      Writing validated records…
                    </div>
                  </div>
                </div>
                <div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: "1.763vh",
  }}
>
  {[
  { 
    label: "Students", 
    count: autoProcessedData?.counts?.students || autoProcessedData?.students?.length || finalPayload?.STUDENTS?.length || 0,
    progress: bulkOnboardProgress?.entities?.students
  },
  { 
    label: "Parents", 
    count: autoProcessedData?.counts?.parents || autoProcessedData?.parents?.length || finalPayload?.PARENTS?.length || 0,
    progress: bulkOnboardProgress?.entities?.parents
  },
  { 
    label: "Staff", 
    count: autoProcessedData?.counts?.staff || autoProcessedData?.staff?.length || finalPayload?.STAFF?.length || 0,
    progress: bulkOnboardProgress?.entities?.staff
  },
  { 
    label: "Fee Arrears", 
    count: autoProcessedData?.counts?.fee_arrears || autoProcessedData?.fee_arrears?.length || finalPayload?.FEE_ARREARS?.length || 0,
    progress: bulkOnboardProgress?.entities?.arrears
  }
].map((r, index) => {
  // Calculate dynamic width based on API progress state
  let barWidth = "0%";
  if (r.progress) {
    if (r.progress.status === 'done') barWidth = "100%";
    else if (r.progress.total > 0 && r.progress.success !== undefined) {
      barWidth = `${Math.min(100, Math.round((r.progress.success / r.progress.total) * 100))}%`;
    } else if (r.progress.status === 'processing') {
      barWidth = `${bulkOnboardProgress?.percent || 10}%`; 
    }
  } else if (bulkOnboardProgress?.percent > 0) {
    barWidth = `${bulkOnboardProgress.percent}%`;
  }

  return (
    <React.Fragment key={index}>
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "0.756vh",
          }}
        >
          <span
            style={{
              fontSize: "1.637vh",
              fontWeight: "700",
              color: "#20242E",
            }}
          >
            {r.label}
          </span>
          <span
            style={{
              font: "700 1.511vh/1 'JetBrains Mono',monospace",
              color: "#5A6173",
            }}
          >
            {r.count}
          </span>
        </div>
        <div
          style={{
            height: "0.756vh",
            borderRadius: "0.378vh",
            background: "#EEF0F6",
            overflow: "hidden",
          }}
        >
          <div style={{ width: barWidth, height: "100%", background: "#4F46E5", transition: "width 0.4s ease-out" }}></div>
        </div>
      </div>
    </React.Fragment>
  );
})}
                </div>
              </div>
            </div>
          </React.Fragment>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>








              {/* <BulkOnboardingResultModal
                isOpen={showResultModal}
                onClose={() => {
                  setShowResultModal(false);
                  setStage("UPLOAD");
                  setAutoProcessedData(null);
                  setUploadedFile(null);
                  setSheets([]);
                  setMappedData([]);
                  setProcessingQueue([]);
                  setCurrentSheetIndex(0);
                }}
                onRetry={() => {
                  setShowResultModal(false);
                  setStage("UPLOAD");
                  setAutoProcessedData(null);
                  setUploadedFile(null);
                  setSheets([]);
                  setMappedData([]);
                  setProcessingQueue([]);
                  setCurrentSheetIndex(0);
                }}
                data={resultModalData}
              /> */}
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default AIManagmentHub;
